/**
 * Image Resizing Utility
 *
 * Resizes images to fit within Anthropic's vision model limits.
 * Also converts unsupported formats (SVG) to supported formats (PNG).
 *
 * Supported formats: PNG, JPEG, GIF, WebP
 * Max size: 5MB per image
 */
// Anthropic's limit is 5MB, we target slightly under for safety
const MAX_IMAGE_SIZE_BYTES = 4.5 * 1024 * 1024; // 4.5MB to be safe
// Maximum dimension for resized images (maintains aspect ratio)
const MAX_DIMENSION = 1568; // Anthropic's recommended max dimension
// Formats supported by Anthropic's vision API
const SUPPORTED_MIME_TYPES = new Set([
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/gif',
    'image/webp',
]);
/**
 * Check if the mime type is supported by Anthropic's vision API
 */
export function isSupportedFormat(mimeType) {
    return SUPPORTED_MIME_TYPES.has(mimeType.toLowerCase());
}
/**
 * Check if the image is SVG format
 */
export function isSvgFormat(mimeType, base64) {
    if (mimeType.toLowerCase() === 'image/svg+xml' || mimeType.toLowerCase() === 'image/svg') {
        return true;
    }
    // Also check base64 content for SVG signature
    if (base64) {
        try {
            const decoded = atob(base64.substring(0, 100)); // Check first ~75 bytes
            return decoded.trim().startsWith('<svg') || decoded.trim().startsWith('<?xml');
        }
        catch {
            return false;
        }
    }
    return false;
}
/**
 * Check if a base64 image exceeds the size limit
 */
export function exceedsSizeLimit(base64) {
    // Base64 is ~4/3 the size of binary, so approximate binary size
    const approximateBytes = (base64.length * 3) / 4;
    return approximateBytes > MAX_IMAGE_SIZE_BYTES;
}
/**
 * Get the approximate size in bytes of a base64 string
 */
export function getBase64SizeBytes(base64) {
    // Remove data URL prefix if present
    const cleanBase64 = base64.replace(/^data:image\/[^;]+;base64,/, '');
    return (cleanBase64.length * 3) / 4;
}
/**
 * Convert and/or resize an image for Anthropic's vision API.
 *
 * Handles:
 * - SVG to PNG conversion (SVG not supported by Anthropic)
 * - Size reduction for images > 4.5MB
 *
 * @param base64 - Base64 encoded image (with or without data URL prefix)
 * @param mimeType - Original MIME type
 * @returns Promise with converted/resized base64 and updated mimeType
 */
export async function resizeImageForVision(base64, mimeType) {
    let currentBase64 = base64;
    let currentMimeType = mimeType;
    let wasConverted = false;
    let wasResized = false;
    // Step 1: Convert SVG to PNG (SVG not supported by Anthropic)
    if (isSvgFormat(mimeType, base64)) {
        console.log(`[ImageResize] Converting SVG to PNG (Anthropic doesn't support SVG)`);
        try {
            const converted = await convertSvgToPng(base64);
            currentBase64 = converted.base64;
            currentMimeType = converted.mimeType;
            wasConverted = true;
            console.log(`[ImageResize] SVG converted to PNG successfully`);
        }
        catch (error) {
            console.error(`[ImageResize] SVG conversion failed:`, error);
            throw new Error(`Cannot convert SVG for vision API: ${error}`);
        }
    }
    // Step 2: Check if resizing is needed
    if (!exceedsSizeLimit(currentBase64)) {
        return { base64: currentBase64, mimeType: currentMimeType, wasResized, wasConverted };
    }
    console.log(`[ImageResize] Image exceeds 4.5MB limit, resizing...`);
    const originalSize = getBase64SizeBytes(currentBase64);
    console.log(`[ImageResize] Original size: ${(originalSize / 1024 / 1024).toFixed(2)}MB`);
    try {
        const resized = await resizeWithCanvas(currentBase64, currentMimeType);
        const newSize = getBase64SizeBytes(resized.base64);
        console.log(`[ImageResize] Resized to: ${(newSize / 1024 / 1024).toFixed(2)}MB`);
        return { ...resized, wasResized: true, wasConverted };
    }
    catch (error) {
        console.warn(`[ImageResize] Canvas resize failed, trying quality reduction:`, error);
        // Fallback: just reduce quality without dimension change
        const fallback = await reduceQualityOnly(currentBase64, currentMimeType);
        return { ...fallback, wasConverted };
    }
}
/**
 * Convert SVG to PNG using Canvas API
 */
async function convertSvgToPng(base64) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            try {
                // Use SVG's natural dimensions or default to reasonable size
                let width = img.naturalWidth || img.width || 512;
                let height = img.naturalHeight || img.height || 512;
                // Cap dimensions if too large
                if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                    const aspectRatio = width / height;
                    if (width > height) {
                        width = MAX_DIMENSION;
                        height = Math.round(width / aspectRatio);
                    }
                    else {
                        height = MAX_DIMENSION;
                        width = Math.round(height * aspectRatio);
                    }
                }
                // Ensure minimum dimensions for very small SVGs
                if (width < 100)
                    width = 512;
                if (height < 100)
                    height = 512;
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Could not get canvas context'));
                    return;
                }
                // Draw SVG to canvas
                ctx.drawImage(img, 0, 0, width, height);
                // Export as PNG (preserves transparency)
                const result = canvas.toDataURL('image/png');
                const base64Only = result.split(',')[1] || result;
                resolve({
                    base64: base64Only,
                    mimeType: 'image/png',
                });
            }
            catch (err) {
                reject(err);
            }
        };
        img.onerror = (e) => {
            reject(new Error(`Failed to load SVG for conversion: ${e}`));
        };
        // Build data URL for SVG
        const dataUrl = base64.startsWith('data:')
            ? base64
            : `data:image/svg+xml;base64,${base64}`;
        img.src = dataUrl;
    });
}
/**
 * Resize image using HTML Canvas API
 */
async function resizeWithCanvas(base64, mimeType) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            try {
                // Calculate new dimensions maintaining aspect ratio
                let { width, height } = img;
                const aspectRatio = width / height;
                if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                    if (width > height) {
                        width = MAX_DIMENSION;
                        height = Math.round(width / aspectRatio);
                    }
                    else {
                        height = MAX_DIMENSION;
                        width = Math.round(height * aspectRatio);
                    }
                }
                // Create canvas and draw resized image
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Could not get canvas context'));
                    return;
                }
                // Use high-quality image smoothing
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, 0, 0, width, height);
                // Convert to JPEG for better compression (unless PNG transparency is needed)
                // Use JPEG for photos, keep PNG for logos/graphics with transparency
                let outputMimeType = mimeType;
                let quality = 0.85;
                // Check if image has transparency
                const hasTransparency = checkTransparency(ctx, width, height);
                if (!hasTransparency && mimeType !== 'image/jpeg') {
                    outputMimeType = 'image/jpeg';
                    console.log(`[ImageResize] Converting to JPEG for better compression`);
                }
                // Iteratively reduce quality if still too large
                let result = canvas.toDataURL(outputMimeType, quality);
                let attempts = 0;
                const maxAttempts = 5;
                while (exceedsSizeLimit(result.split(',')[1] || result) && attempts < maxAttempts) {
                    quality -= 0.15;
                    if (quality < 0.3) {
                        // If quality is too low, reduce dimensions further
                        const scale = 0.75;
                        canvas.width = Math.round(canvas.width * scale);
                        canvas.height = Math.round(canvas.height * scale);
                        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                        quality = 0.7;
                    }
                    result = canvas.toDataURL(outputMimeType, quality);
                    attempts++;
                    console.log(`[ImageResize] Attempt ${attempts}: quality=${quality.toFixed(2)}, size=${(getBase64SizeBytes(result.split(',')[1] || result) / 1024 / 1024).toFixed(2)}MB`);
                }
                // Remove data URL prefix to return just base64
                const base64Only = result.split(',')[1] || result;
                resolve({
                    base64: base64Only,
                    mimeType: outputMimeType,
                });
            }
            catch (err) {
                reject(err);
            }
        };
        img.onerror = () => {
            reject(new Error('Failed to load image for resizing'));
        };
        // Add data URL prefix if not present
        const dataUrl = base64.startsWith('data:')
            ? base64
            : `data:${mimeType};base64,${base64}`;
        img.src = dataUrl;
    });
}
/**
 * Check if an image has transparency by sampling pixels
 */
function checkTransparency(ctx, width, height) {
    try {
        // Sample corners and center
        const points = [
            [0, 0],
            [width - 1, 0],
            [0, height - 1],
            [width - 1, height - 1],
            [Math.floor(width / 2), Math.floor(height / 2)],
        ];
        for (const [x, y] of points) {
            const pixel = ctx.getImageData(x, y, 1, 1).data;
            if (pixel[3] < 255) {
                return true; // Has transparency
            }
        }
        return false;
    }
    catch {
        // If we can't check, assume no transparency
        return false;
    }
}
/**
 * Fallback: reduce quality without dimension change
 */
async function reduceQualityOnly(base64, mimeType) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                // Can't resize, return original
                resolve({ base64, mimeType, wasResized: false });
                return;
            }
            ctx.drawImage(img, 0, 0);
            // Convert to JPEG at reduced quality (unless it needs transparency)
            const hasTransparency = checkTransparency(ctx, img.width, img.height);
            const outputMime = hasTransparency ? 'image/png' : 'image/jpeg';
            const quality = hasTransparency ? undefined : 0.6;
            const result = canvas.toDataURL(outputMime, quality);
            const base64Only = result.split(',')[1] || result;
            resolve({
                base64: base64Only,
                mimeType: outputMime,
                wasResized: true,
            });
        };
        img.onerror = () => {
            resolve({ base64, mimeType, wasResized: false });
        };
        const dataUrl = base64.startsWith('data:')
            ? base64
            : `data:${mimeType};base64,${base64}`;
        img.src = dataUrl;
    });
}
